import {
  CheckboxField,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@aws-amplify/ui-react"
import { formatUnicorn } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useCallback, useRef, useState, type ChangeEvent } from "react"
import { FaFileExcel, FaFilter, FaTrashCan, FaXmark } from "react-icons/fa6"
import { useParams } from "react-router-dom"
import {
  deleteFeedingReports,
  getFeedingReport,
} from "../../../api/FeedingReportsApi"
import Modal from "../../../components/Modal"
import {
  ButtonWithIcon,
  SelectButtonWithIcon,
} from "../../../components/ButtonWithIcon"
import { isProd } from "../../../config"
import { runFeedingsExport } from "../../../features/export"
import { UNIT_OF_MASS } from "../../../org-settings"
import {
  FeedingStatsPage,
  type FeedingStatsRenderComponentProps,
} from "../-components/FeedingStatsPage"
import { ERROR_THRES } from "./StatsSettings"

export default function Feedings() {
  const title = "Fütterungen"

  return (
    <FeedingStatsPage
      title={title}
      isDataPresent={(feedingStats: any): boolean =>
        feedingStats && feedingStats.meta
      }
      RenderComponent={FeedingsRenderComponent}
    />
  )
}

function FeedingsRenderComponent({
  feedingStats,
  navigate,
  reload,
}: FeedingStatsRenderComponentProps) {
  const params = useParams<{ orgId: string }>()
  const orgId = params.orgId!
  const [selectedDriver, setSelectedDriver] = useState<string | undefined>(
    undefined
  )
  const [selectedRecipename, setSelectedRecipename] = useState<
    string | undefined
  >(undefined)

  const [state, setState] = useState<"idle" | "running" | "done" | "error">(
    "idle"
  )
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const abortRef = useRef<AbortController | null>(null)

  const [selectedIds, setSelectedIds] = useState(new Set<string>())
  const allSelected =
    feedingStats.SingleFeedings.length > 0 &&
    selectedIds.size === feedingStats.SingleFeedings.length
  const someSelected = selectedIds.size > 0 && !allSelected // indeterminate

  const drivers = [
    ...new Set<string>(
      feedingStats.SingleFeedings.map((feeding: any) => feeding.Driver)
    ),
  ].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
  )
  const recipeNames = [
    ...new Set<string>(
      feedingStats.SingleFeedings.map((feeding: any) => feeding.Recipename)
    ),
  ].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
  )
  !isProd && console.debug("selectedDriver=", JSON.stringify(selectedDriver))
  !isProd &&
    console.debug("selectedRecipename=", JSON.stringify(selectedRecipename))

  // todo: filter feedings here, based on driver, recipe name

  const sortedFeedings = feedingStats.SingleFeedings.sort((a: any, b: any) => {
    return a.Start < b.Start ? 1 : -1
  })

  const handleSelectedDriverChanged = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    event.preventDefault()
    setSelectedDriver(
      event.target.value === "   " ? undefined : event.target.value
    )
  }
  const handleSelectedRecipenameChanged = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    event.preventDefault()
    setSelectedRecipename(
      event.target.value === "   " ? undefined : event.target.value
    )
  }

  const handleExport = useCallback(async () => {
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setState("running")
    setProgress({ done: 0, total: sortedFeedings.length })
    try {
      await runFeedingsExport(
        sortedFeedings,
        async (feedingReportId: string) => {
          console.debug("async get details: ", orgId, feedingReportId)
          return await getFeedingReport(orgId, feedingReportId)
        },
        (done, total) => setProgress({ done, total }),
        ctrl.signal
      )
      setState("done")
    } catch (e) {
      if (!ctrl.signal.aborted) {
        console.error(e)
        setState("error")
      }
    }
  }, [])

  const handleCancel = useCallback(() => {
    abortRef.current?.abort()
    setState("idle")
    setProgress({ done: 0, total: 0 })
  }, [])

  const running = state === "running"

  const handleHeaderCheckbox = () => {
    // Indeterminate or none → select all; All selected → clear
    if (allSelected) {
      setSelectedIds(new Set<string>())
    } else {
      setSelectedIds(
        new Set<string>(
          feedingStats.SingleFeedings.map(
            (feeding: any) => feeding.FeedingReportId
          )
        )
      )
    }
  }

  const handleRowCheckbox = (feedingReportId: string) => {
    setSelectedIds((prev) => {
      const next = new Set<string>(prev)
      next.has(feedingReportId)
        ? next.delete(feedingReportId)
        : next.add(feedingReportId)
      return next
    })
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined)

  const handleBulkDelete = () => {
    setShowDeleteModal(true)
  }

  const deleteModalCallback = async (value: string) => {
    if (value.trim().toLowerCase() === "ja") {
      const ids = [...selectedIds]
      console.log("Deleting", ids)
      const results = await deleteFeedingReports(orgId, ids)
      // "not-found" is treated as success too: the desired end state (gone
      // from the live list) already holds. Only "failed" is left selected,
      // so the user can see what didn't go through and retry.
      const succeededIds = new Set(
        results
          .filter((r) => r.status === "deleted" || r.status === "not-found")
          .map((r) => r.feedingReportId)
      )
      const failedCount = results.length - succeededIds.size

      setSelectedIds((prev) => {
        const next = new Set(prev)
        succeededIds.forEach((id) => next.delete(id))
        return next
      })
      setDeleteError(
        failedCount > 0
          ? formatUnicorn(
              I18n.get(
                "{failedCount} von {totalCount} Fütterungen konnten nicht gelöscht werden. Bitte versuchen Sie es erneut."
              ),
              { failedCount, totalCount: results.length }
            )
          : undefined
      )
      if (succeededIds.size > 0) {
        reload()
      }
    }
    setShowDeleteModal(false)
  }

  return (
    <div className="rm-list-container">
      <Flex
        className="mb-2 !flex-col !items-start gap-2 md:!flex-row md:!gap-0"
        justifyContent={"space-between"}
        alignItems="flex-start"
      >
        <div>
          Von{" "}
          <u>
            {new Date(feedingStats.meta.begin).toLocaleString("de-de", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </u>{" "}
          bis{" "}
          <u>
            {new Date(feedingStats.meta.end).toLocaleString("de-de", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </u>
        </div>

        <Flex className="!flex-wrap !justify-end gap-2 !self-end md:!self-auto">
          <SelectButtonWithIcon
            icon={<FaFilter />}
            onChange={handleSelectedRecipenameChanged}
            options={[
              { text: I18n.get("Alle Rezepte"), value: "   " },
              ...recipeNames.map((r: string) => {
                return { text: r, value: r }
              }),
            ]}
          />
          <SelectButtonWithIcon
            icon={<FaFilter />}
            onChange={handleSelectedDriverChanged}
            options={[
              { text: I18n.get("Alle Fahrer"), value: "   " },
              ...drivers.map((d: string) => {
                return { text: d, value: d }
              }),
            ]}
          />
          <ButtonWithIcon
            title={I18n.get("Export")}
            icon={<FaFileExcel />}
            onClick={handleExport}
            isDisabled={running}
          />
          <ButtonWithIcon
            title={`${I18n.get("Löschen")} (${selectedIds.size})`}
            icon={<FaTrashCan />}
            onClick={handleBulkDelete}
            isDisabled={selectedIds.size === 0}
          />
          {running && (
            <ButtonWithIcon
              title={I18n.get("Abbrechen")}
              icon={<FaXmark />}
              onClick={handleCancel}
            />
          )}
        </Flex>
      </Flex>

      {running && (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <svg
              width={20}
              height={20}
              viewBox="0 0 20 20"
              style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}
            >
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              <circle
                cx={10}
                cy={10}
                r={8}
                fill="none"
                stroke="#e0e6f0"
                strokeWidth={3}
              />
              <path
                d="M10 2 a8 8 0 0 1 8 8"
                fill="none"
                stroke="#1f3864"
                strokeWidth={3}
                strokeLinecap="round"
              />
            </svg>
            <span style={{ fontSize: 14, color: "#333" }}>
              {I18n.get("Lade Details…")} ({progress.done} / {progress.total})
            </span>
          </div>
          <div style={{ display: "flex", gap: 2, height: 6 }}>
            {sortedFeedings.map((_: any, i: any) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  borderRadius: 3,
                  background: i < progress.done ? "#1f3864" : "#e0e6f0",
                  transition: "background .2s",
                }}
              />
            ))}
          </div>
        </div>
      )}

      {state === "done" && (
        <div className="export-banner export-banner-success">
          <span>
            ✓{" "}
            {I18n.get(
              "Datei gespeichert. Schauen Sie in Ihren Downloads-Ordner."
            )}
          </span>
          <button
            onClick={() => setState("idle")}
            className="export-banner-close"
            aria-label={I18n.get("Schließen")}
          >
            <FaXmark className="inline" />
          </button>
        </div>
      )}

      {state === "error" && (
        <div className="export-banner export-banner-error">
          <span>
            ✗ {I18n.get("Export fehlgeschlagen. Versuchen Sie es noch einmal.")}
          </span>
          <button
            onClick={() => setState("idle")}
            className="export-banner-close"
            aria-label={I18n.get("Schließen")}
          >
            <FaXmark className="inline" />
          </button>
        </div>
      )}

      {deleteError && (
        <div className="export-banner export-banner-error">
          <span>✗ {deleteError}</span>
          <button
            onClick={() => setDeleteError(undefined)}
            className="export-banner-close"
            aria-label={I18n.get("Schließen")}
          >
            <FaXmark className="inline" />
          </button>
        </div>
      )}

      {showDeleteModal && (
        <Modal
          buttons={[{ text: "Ja", variation: "destructive" }, { text: "Nein" }]}
          show={showDeleteModal}
          callback={deleteModalCallback}
        >
          <div className="title">Fütterungen wirklich löschen?</div>
          <div>
            Wollen Sie {selectedIds.size}{" "}
            {selectedIds.size === 1 ? "Fütterung" : "Fütterungen"} wirklich
            löschen?
          </div>
          <div>
            Gelöschte Fütterungen können innerhalb von 7 Tagen im Papierkorb
            wiederhergestellt werden.
          </div>
        </Modal>
      )}

      <div className="overflow-x-auto">
        <Table highlightOnHover={true} size="small" variation="striped">
          <TableHead>
            <TableRow className="bg-gray-300">
              <TableCell
                as="th"
                onClick={(e) => {
                  e.stopPropagation()
                  handleHeaderCheckbox()
                }}
                className="cursor-pointer"
              >
                <CheckboxField
                  label=""
                  labelHidden
                  name="select-all"
                  value="all"
                  checked={allSelected}
                  isIndeterminate={someSelected}
                  readOnly
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              <TableCell as="th">Start, Ende</TableCell>
              <TableCell as="th">Rezeptname</TableCell>
              <TableCell as="th">Fahrer</TableCell>
              <TableCell as="th">Geplant</TableCell>
              <TableCell as="th">Ladung</TableCell>
              <TableCell as="th">
                Fehler {/* (Ladung Soll vs Ladung Ist +/-) */}
              </TableCell>
              <TableCell as="th">Abladung</TableCell>
              <TableCell as="th">
                Fehler {/* (Ladung Soll vs Abladung Ist +/-) */}
              </TableCell>
              <TableCell as="th">Arb. Zeit</TableCell>
              {/* <TableCell as="th">Kosten</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody style={{ cursor: "pointer" }}>
            {sortedFeedings.map((feeding: any) => {
              if (selectedDriver && feeding.Driver !== selectedDriver) {
                return []
              }
              if (
                selectedRecipename &&
                feeding.Recipename !== selectedRecipename
              ) {
                return []
              }
              const href = "./" + feeding.FeedingReportId.replace("#", "||")
              const highLoadingError =
                Math.abs(feeding.LoadingRatio) > ERROR_THRES
              const highUnloadingError =
                Math.abs(feeding.UnloadingRatio) > ERROR_THRES
              const highError = highLoadingError || highUnloadingError
              return (
                <TableRow
                  key={feeding.FeedingReportId}
                  onClick={() => navigate(href)}
                  className={[
                    selectedIds.has(feeding.FeedingReportId)
                      ? "!bg-green-50"
                      : "",
                    highError ? "!bg-red-100" : "",
                  ]
                    .join(" ")
                    .trim()}
                >
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRowCheckbox(feeding.FeedingReportId)
                    }}
                    className="cursor-pointer"
                  >
                    <CheckboxField
                      label=""
                      labelHidden
                      name={`select-${feeding.FeedingReportId}`}
                      value={feeding.FeedingReportId}
                      checked={selectedIds.has(feeding.FeedingReportId)}
                      readOnly
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(feeding.Start).toLocaleDateString("de-de", {
                      dateStyle: "short",
                    })}{" "}
                    {new Date(feeding.Start).toLocaleTimeString("de-de", {
                      timeStyle: "short",
                    })}
                    {" - "}
                    {new Date(feeding.End).toLocaleTimeString("de-de", {
                      timeStyle: "short",
                    })}
                  </TableCell>
                  <TableCell>{feeding.Recipename}</TableCell>
                  <TableCell>{feeding.Driver}</TableCell>
                  <TableCell>
                    {feeding.LoadingTarget.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                    {` ${UNIT_OF_MASS}`}
                  </TableCell>
                  <TableCell>
                    {feeding.Loaded.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                    {` ${UNIT_OF_MASS}`}
                  </TableCell>
                  <TableCell className={highLoadingError ? "!bg-red-200" : ""}>
                    {feeding.LoadingRatio >= 0 && (
                      <span style={{ visibility: "hidden", opacity: 0 }}>
                        -
                      </span>
                    )}
                    {feeding.LoadingRatio.toLocaleString(undefined, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}{" "}
                    %
                  </TableCell>
                  <TableCell>
                    {feeding.Unloaded.toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })}
                    {` ${UNIT_OF_MASS}`}
                  </TableCell>
                  <TableCell
                    className={highUnloadingError ? "!bg-red-200" : ""}
                  >
                    {feeding.UnloadingRatio >= 0 && (
                      <span style={{ visibility: "hidden", opacity: 0 }}>
                        -
                      </span>
                    )}
                    {feeding.UnloadingRatio.toLocaleString(undefined, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}{" "}
                    %
                  </TableCell>
                  <TableCell>{feeding.Duration} Min.</TableCell>
                  {/* <TableCell>n/a {CURRENCY_SYMBOL}</TableCell> */}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
